/**
 * Ranking em barras horizontais — a forma padrão de "quem aparece mais" no
 * painel: rotas, países, cidades, navegadores, operadoras.
 *
 * Barra horizontal (e não coluna) porque os rótulos são longos e de tamanho
 * irregular: na vertical eles virariam texto inclinado ou cortado.
 *
 * Cor é UMA só: a comparação aqui é de magnitude, e o comprimento da barra já
 * a carrega inteira. Pintar cada linha de uma cor sugeriria uma diferença de
 * categoria que não existe.
 */

/** Uma linha do ranking. */
export interface BreakdownItem {
  label: string;
  value: number;
  /** Informação de apoio à direita do rótulo (ex.: "12 visitantes"). */
  detail?: string;
}

interface BreakdownBarsProps {
  title: string;
  description?: string;
  items: BreakdownItem[];
  formatValue?: (value: number) => string;
  emptyMessage?: string;
  /** Quantas linhas mostrar (o resto vira "outros"). */
  limit?: number;
}

const DEFAULT_LIMIT = 10;

export function BreakdownBars({
  title,
  description,
  items,
  formatValue = (value) => value.toLocaleString('pt-BR'),
  emptyMessage = 'Nenhum acesso no período.',
  limit = DEFAULT_LIMIT,
}: BreakdownBarsProps) {
  const visible = items.slice(0, limit);
  const rest = items.slice(limit);
  const restTotal = rest.reduce((total, item) => total + item.value, 0);

  // A escala é o MAIOR item mostrado, não o total: comparar a maior barra com
  // a soma de todas deixaria todo ranking com uma barra cheia e o resto rente
  // a zero.
  const maxValue = Math.max(1, ...visible.map((item) => item.value));

  return (
    <section className="breakdown">
      <header className="breakdown__header">
        <h3 className="breakdown__title">{title}</h3>
        {description && <p className="breakdown__description">{description}</p>}
      </header>

      {visible.length === 0 ? (
        <p className="empty-state">{emptyMessage}</p>
      ) : (
        <ol className="breakdown__list">
          {visible.map((item) => (
            <li className="breakdown__item" key={item.label}>
              <div className="breakdown__labels">
                <span className="breakdown__label" title={item.label}>
                  {item.label}
                </span>
                <span className="breakdown__value">{formatValue(item.value)}</span>
              </div>
              <div className="breakdown__track">
                <div
                  className="breakdown__bar"
                  style={{ width: `${Math.max(2, (item.value / maxValue) * 100)}%` }}
                />
              </div>
              {item.detail && <span className="breakdown__detail">{item.detail}</span>}
            </li>
          ))}
        </ol>
      )}

      {rest.length > 0 && (
        <p className="breakdown__footer">
          + {rest.length} {rest.length === 1 ? 'outro' : 'outros'} somando {formatValue(restTotal)}
        </p>
      )}
    </section>
  );
}
